import type { StateCreator } from 'zustand';
import type { UserSettings } from '../../types';
import type { StoreState } from '../useStore';
import * as dataApi from '../../services/dataApi';
import { normalizeSettings } from '../normalizers';
import { reportError, trackBreadcrumb } from '@/services/errorTracking';
import { defaultSettings } from '../defaults';
import { isOfflinePersistenceError, queueOfflineSettingsUpdate } from '@/utils/offlineSync';

export interface SettingsSlice {
  settings: UserSettings;
  setSettings: (settings: UserSettings) => void;
  updateSettings: (settings: Partial<UserSettings>) => Promise<void>;
  completeOnboarding: () => Promise<void>;
}

export const createSettingsSlice: StateCreator<StoreState, [], [], SettingsSlice> = (set, get) => ({
  settings: { ...defaultSettings },
  setSettings: (settings) => set({ settings }),

  updateSettings: async (updates) => {
    // Capture only the fields being changed for targeted rollback
    const prev = get().settings;
    const rollbackFields: Partial<UserSettings> = {};
    for (const key of Object.keys(updates) as Array<keyof UserSettings>) {
      // TypeScript can't narrow the generic index assignment here because
      // `updates` is `Partial<UserSettings>` (some fields may be optional).
      // The loop is safe — we only copy fields the caller is actually
      // changing — so we assign through an indexed signature cast.
      (rollbackFields as Record<keyof UserSettings, UserSettings[keyof UserSettings]>)[key] =
        prev[key];
    }
    // Optimistic update
    set((state) => ({
      settings: { ...state.settings, ...updates },
    }));
    const { reviewHistory: _, ...apiSettings } = updates;
    try {
      const res = (await dataApi.updateUserSettings(apiSettings)) as Record<string, unknown>;
      set((state) => ({
        settings: {
          ...state.settings,
          ...normalizeSettings(res),
        },
      }));
    } catch (err) {
      if (isOfflinePersistenceError(err)) {
        // Keep the optimistic settings in place during offline review flow.
        // The queued delta is replayed in order by syncOfflineReviews when
        // connectivity returns.
        queueOfflineSettingsUpdate({ updates: apiSettings as Record<string, unknown> });
        // Expected success-path when offline; not reportError (Vidi-02m).
        trackBreadcrumb('offline', 'Settings update queued for offline sync');
        return;
      }
      // Rollback only the fields we changed, preserving concurrent updates
      set((state) => ({
        settings: { ...state.settings, ...rollbackFields },
      }));
      reportError(err, { operation: 'updateSettings' });
      throw err;
    }
  },

  completeOnboarding: async () => {
    const newSettings = { ...get().settings, onboardingCompleted: true };
    set({ settings: newSettings });
    try {
      await dataApi.updateUserSettings({ onboardingCompleted: true });
    } catch {
      // ignore
    }
  },
});
